import React, { useState, useMemo } from 'react';

type Role = 'buyer' | 'seller';

interface Step {
  title: string;
  description: string;
  icon?: string; // remix icon name
}

interface OnboardingTourProps {
  role: Role;
  onClose: () => void;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ role, onClose }) => {
  const steps = useMemo<Step[]>(() => {
    if (role === 'seller') {
      return [
        { title: 'Welcome to Seller Dashboard', description: 'Manage your listings, orders and messages here.', icon: 'ri-store-line' },
        { title: 'Post a Listing', description: 'Go to Sell, add title, price, photos and post.', icon: 'ri-upload-cloud-2-line' },
        { title: 'Manage Orders', description: 'Accept orders, mark progress and complete safely.', icon: 'ri-file-list-3-line' },
        { title: 'Messages', description: 'Chat with buyers to answer questions and arrange meetups.', icon: 'ri-chat-3-line' },
        { title: 'Earnings', description: 'Track your earnings and listing performance.', icon: 'ri-money-dollar-circle-line' },
      ];
    }
    return [
      { title: 'Welcome to Buyer Dashboard', description: 'Discover items from students, hostels and more.', icon: 'ri-dashboard-line' },
      { title: 'Browse Marketplace', description: 'Use filters and categories. Neatly Used shows non-new items.', icon: 'ri-shopping-bag-3-line' },
      { title: 'View Hostels', description: 'Explore hostel/accommodation listings and contact sellers.', icon: 'ri-home-4-line' },
      { title: 'Place Orders', description: 'Open a product, tap Buy or Message to proceed.', icon: 'ri-shopping-cart-line' },
      { title: 'Complete Orders', description: 'Follow order status updates and complete after pickup/delivery.', icon: 'ri-checkbox-circle-line' },
    ];
  }, [role]);

  const [index, setIndex] = useState(0);
  const current = steps[index];
  const isLast = index === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[1000] flex items-start sm:items-center justify-center bg-black/40 p-2 sm:p-4">
      <div className="w-full max-w-lg sm:max-w-xl bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Quick Tour</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 cursor-pointer" aria-label="Close">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <div className="px-5 py-6">
          <div className="flex items-start gap-3">
            {current.icon && (
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-50 text-teal-600">
                <i className={`${current.icon} text-xl`}></i>
              </div>
            )}
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-1">{current.title}</h4>
              <p className="text-sm text-gray-600">{current.description}</p>
            </div>
          </div>

          {/* Progress dots */}
          <div className="mt-6 flex items-center gap-1">
            {steps.map((_, i) => (
              <span key={i} className={`w-2 h-2 rounded-full ${i === index ? 'bg-teal-600' : 'bg-gray-300'}`}></span>
            ))}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row-reverse gap-2">
            <button
              onClick={() => { if (isLast) onClose(); else setIndex(i => Math.min(i + 1, steps.length - 1)); }}
              className="w-full sm:w-auto px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 cursor-pointer"
            >
              {isLast ? 'Finish' : 'Next'}
            </button>
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 cursor-pointer"
            >
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;